import cx from 'classnames';
import { noop } from 'lib/f';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Ratio } from 'components/@tedui';
import Image from 'components/image';
import { Link } from 'components/router';
import { Heading, Microcopy, Text } from 'components/typography';

export type PlaylistCardSizes = 'l' | 's';

type PlaylistCardProps = {
  priority?: boolean;
  className?: string;
  href: string;
  imageUrl: string;
  size?: PlaylistCardSizes;
  title: string;
  description?: string;
  author?: string;
  talksCount: number;
  onClick?: () => void;
};

const PlaylistCard = ({
  className = '',
  href,
  imageUrl,
  size = 's',
  title,
  description,
  author,
  talksCount,
  onClick = noop,
  priority,
  ...props
}: PlaylistCardProps): React.ReactNode => {
  const isLargeCard = size === 'l';
  const isSmallCard = size === 's';

  const cardWrapperClassName = cx('group', {
    'flex md:block': isLargeCard,
    block: isSmallCard,
    [className]: className
  });

  const imageClassName = cx({
    'w-1/2 shrink-0 md:w-auto': isLargeCard
  });

  const textWrapperClassName = cx('pr-2', {
    'pl-4 md:pl-0 md:pt-4 xl:pr-8 xl:pt-5': isLargeCard,
    'pt-3 md:pr-3 md:pt-5 xl:pr-2 xl:pt-4': isSmallCard
  });

  const presenterClassName = cx('font-bold uppercase text-gray-500', {
    'mb-1 xl:mb-2': isLargeCard,
    'mb-1': isSmallCard
  });

  const headingClassName = cx('transition-colors group-hover:text-gray-700', {
    'mb-3 md:mb-4': description || author
  });

  const descriptionClassName = cx('text-gray-900', {
    'mb-4 md:mb-5': author && isLargeCard,
    'mb-4': author && isSmallCard,
    'hidden md:block': isSmallCard
  });

  return (
    <Link
      href={href}
      className={cardWrapperClassName}
      onClick={onClick}
      {...props}
    >
      <div className={imageClassName} aria-hidden>
        <Ratio ratio={[1, 1]}>
          <div className="relative h-full">
            <Image
              alt={title}
              src={imageUrl}
              priority={priority}
              fill
              sizes="300px"
              className={cx('card-image size-full bg-gray-300 object-cover')}
            />
          </div>
        </Ratio>
      </div>
      <div className={textWrapperClassName}>
        {talksCount && (
          <Microcopy size="s" className={presenterClassName}>
            <FormattedMessage
              defaultMessage="{talksCount, plural, one {# talk} other {# talks}}"
              values={{
                talksCount
              }}
            />
          </Microcopy>
        )}
        <Heading size={isLargeCard ? 'm' : 's'} className={headingClassName}>
          {title}
        </Heading>
        {description && (
          <Text size="m" className={descriptionClassName}>
            {description}
          </Text>
        )}
        {author && (
          <Text size="s" className="text-gray-500">
            <FormattedMessage defaultMessage="Curated by" />
            {` `}
            {author}
          </Text>
        )}
      </div>
    </Link>
  );
};

export default PlaylistCard;
